import { defineStore, storeToRefs } from "pinia";
import { computed, ref } from "vue";
import useMyFetch from "@stores/useMyFetch";

import { getRegionOptions } from "@utils/constants/regions";
import { computedCardOptions, optionToCardToken, cardTokenToOption } from "@utils/computeCardOptions";
import { loopTokensByCardToken } from "@stores/survey/computeCardsList";

import genToken from "@utils/generateToken";
import { deepCopy, uniq, isEmpty } from "@utils/toolBox";

import { useSurveyStore } from "@stores/survey/survey";

import { useSurveyCardsStore } from "@stores/survey/surveyCards";
import { useQuotasStore } from "@stores/survey/quotas";
import { conditionCardToken } from "@utils/conditionData";
import { computedValuesOptions } from "@utils/computeConditionValuesOptions";
import { captureEvent } from "@/lib/analytics";

export const useSurveyTargetsStore = defineStore("surveyTargets", () => {
  const surveyStore = useSurveyStore();
  const { survey } = storeToRefs(surveyStore);

  const surveyCardsStore = useSurveyCardsStore();
  const { findCardByToken } = surveyCardsStore;

  const targets = ref([]);
  const initialTargets = ref([]);
  const selectedTarget = ref({});
  const deletedTargets = ref([]);
  const fetchedSurveyToken = ref(null);

  async function fetch(token) {
    fetchedSurveyToken.value = null;
    showValidation.value = false;
    const { data } = await useMyFetch(`/survey_targets/survey_index?survey_token=${token}`).json();
    hydrate(data.value, token);
    return targets.value;
  }

  function hydrate(list, token) {
    // Ensure every condition has a stable token for Vue :key bindings
    list.forEach((target) => {
      (target.conditions ?? []).forEach((condition) => {
        if (!condition.token) condition.token = genToken();
      });
    });
    initialTargets.value = deepCopy(list);
    targets.value = list;
    selectedTarget.value = targets.value[0] ?? {};
    fetchedSurveyToken.value = token;
  }

  async function save() {
    showValidation.value = true;
    if (hasTargetsErrors.value) return;
    // Never save targets that belong to another survey (stale store).
    if (fetchedSurveyToken.value !== survey.value.token) return;

    let allSaved = true;
    for (const target of targets.value) {
      const { data, response } = await useMyFetch(`/survey_targets/${target.token}`).patch({ target }).json();
      if (response.value.ok) {
        const targetIndex = targets.value.findIndex((t) => t.token === target.token);
        Object.assign(targets.value[targetIndex], data.value);
      } else allSaved = false;
    }

    initialTargets.value = deepCopy(targets.value);
    const deletedTokens = [];
    await Promise.all(
      deletedTargets.value.map(async (target) => {
        const { response } = await useMyFetch(`/survey_targets/${target.token}`).delete({ target }).json();
        if (response.value.ok) deletedTokens.push(target.token);
      }),
    );
    deletedTargets.value = deletedTargets.value.filter((t) => !deletedTokens.includes(t.token));
    showValidation.value = false;
    const deletionsFailed = deletedTargets.value.length > 0;

    if (allSaved && !deletionsFailed) {
      captureEvent("survey_targets_saved", {
        targets_count: targets.value.length,
        deleted_count: deletedTokens.length,
      });
    }
  }

  // country
  const countryOptions = computed(() => getRegionOptions());
  const updateTargetCountries = (index, value) => {
    targets.value[index].countries = value;
  };

  // targets
  const currentTargets = computed(() => targets.value);
  const hasTargetsChanged = computed(() => JSON.stringify(initialTargets.value) !== JSON.stringify(targets.value));
  const hasSingleTarget = computed(() => targets.value.length === 1);
  const targetCountries = computed(() => uniq(targets.value?.map(({ countries }) => countries)).flat());
  const isMultiCountry = computed(() => targetCountries.value?.length > 1);

  const addNewTarget = () => {
    const newTarget = createNewTarget(targets.value.length);
    targets.value.push(newTarget);
    selectedTarget.value = newTarget;
  };

  const duplicateTarget = async (targetToken) => {
    const { data, response } = await useMyFetch(`/survey_targets/${targetToken}/duplicate`).post().json();
    if (!response.value.ok) return;

    const sourceIndex = targets.value.findIndex((t) => t.token === targetToken);
    targets.value.splice(sourceIndex + 1, 0, data.value);
    initialTargets.value.splice(sourceIndex + 1, 0, deepCopy(data.value));
    selectedTarget.value = data.value;

    captureEvent("survey_target_duplicated");

    await useQuotasStore().fetchQuotas();
  };

  const createNewTarget = (index) => {
    return {
      name: `Target ${index + 1}`,
      token: genToken(),
      conditions: [],
      survey_id: survey.value.id,
      crm_id: "",
    };
  };

  const updateSelectedTarget = (targetToken) => {
    selectedTarget.value = currentTargets.value.find((t) => t.token === targetToken);
  };
  const removeTarget = (targetToken) => {
    let deletedTargetIndex = targets.value.findIndex((t) => t.token === targetToken);
    const target = targets.value[deletedTargetIndex];
    if (target.id) deletedTargets.value.push(target);
    useQuotasStore().discardQuotasFor(targetToken);
    targets.value.splice(deletedTargetIndex, 1);
    if (deletedTargetIndex === 0) {
      selectedTarget.value = targets.value[0] ?? {};
    } else {
      selectedTarget.value = targets.value[deletedTargetIndex - 1] ?? {};
    }
    return targets.value;
  };

  const isTargetSelected = (token) => token === selectedTarget.value?.token;

  const selectedTargetIndex = computed(() =>
    currentTargets.value.findIndex((t) => t.token === selectedTarget.value?.token),
  );

  const isCardBindedToTarget = (cardToken) => currentConditionsQuestions.value.flat().includes(cardToken);

  // CONDITIONS
  const currentTargetsConditions = computed(() => {
    return currentTargets.value.map((target) => target.conditions ?? []);
  });

  const createNewTargetCondition = () => ({
    token: genToken(),
    card_token: "",
    operator: "or",
    type: "is",
    values: [],
  });

  const handleAddCondition = (index) => currentTargetsConditions.value[index].push(createNewTargetCondition());
  const removeCondition = (index, indexCondition) => {
    return currentTargetsConditions.value[index].splice(indexCondition, 1);
  };

  // question
  const currentConditionsQuestions = computed(() =>
    currentTargetsConditions.value?.map((conditions) => {
      if (!isEmpty(conditions)) {
        return conditions.map((condition) => condition?.card_token);
      }
      return [];
    }),
  );
  const conditionQuestionModel = computed(() => {
    return currentConditionsQuestions.value.map((cardTokensList) => {
      if (isEmpty(cardTokensList)) return cardTokensList;
      return cardTokensList.map((cardToken) => {
        if (cardToken) return cardTokenToOption(cardToken);
        return null;
      });
    });
  });

  const conditionQuestionOptions = computed(() => {
    const allowedTypes = [
      "q_single",
      "q_multiple",
      "q_hidden",
      "q_image_choice",
      "q_image_choice_multiple",
      "q_ranking",
    ];
    const cardsList = surveyCardsStore.cards;
    const loopTokens = loopTokensByCardToken(cardsList);
    const validCards = cardsList.filter(
      (c) => allowedTypes.includes(c.type) && c.name && loopTokens.get(c.token) == null,
    );
    return computedCardOptions({ cards: validCards });
  });

  const updateConditionQuestion = (condition, value) => {
    condition.card_token = optionToCardToken(value);
    updateConditionValues(condition, []);
  };

  // type
  const typeOptions = ref([
    { label: "IS", value: "is" },
    { label: "IS NOT", value: "is_not" },
  ]);

  const updateConditionType = (condition, value) => (condition.type = value);

  // values
  const conditionValuesOptions = ({ card_token }) => {
    const card = findCardByToken({ token: conditionCardToken(card_token) });
    if (!card) return [];

    const sourceCard = card.type === "o_custom" ? findCardByToken({ token: card.specs?.question_reference }) : undefined;
    return computedValuesOptions(card, sourceCard);
  };
  const updateConditionValues = (condition, values) => (condition.values = values);

  // operator
  const operatorOptions = ref([
    { label: "OR", value: "or" },
    { label: "AND", value: "and" },
  ]);
  const updateOperator = (condition, value) => (condition.operator = value);

  // errors
  const showValidation = ref(false);
  const targetsValidationErrors = computed(() => {
    const errors = [];
    targets.value.forEach((target, index) => {
      if (!target.name?.trim()) {
        errors.push({ path: target.token, name: "name", message: "Name cannot be blank" });
      }
      if (!target.countries?.length) {
        errors.push({ path: target.token, name: "countries", message: "Countries cannot be blank" });
      }
      if (!target.target || target.target <= 0) {
        errors.push({ path: target.token, name: "target", message: "Target must be greater than 0" });
      }
      const conditions = target.conditions ?? [];
      if (conditions.length && conditions.some((c) => c.card_token && !c.values?.length)) {
        errors.push({ path: target.token, name: "conditions", message: "Condition values cannot be blank" });
      }
      // Duplicate targets (same conditions + countries)
      const sortedCountries = JSON.stringify([...(target.countries ?? [])].sort());
      const sortedConditions = JSON.stringify(
        conditions.map((c) => [c.card_token, c.operator, c.type, [...(c.values ?? [])].sort()]).sort(),
      );
      for (let i = 0; i < index; i++) {
        const other = targets.value[i];
        const otherCountries = JSON.stringify([...(other.countries ?? [])].sort());
        const otherConditions = JSON.stringify(
          (other.conditions ?? []).map((c) => [c.card_token, c.operator, c.type, [...(c.values ?? [])].sort()]).sort(),
        );
        if (sortedCountries === otherCountries && sortedConditions === otherConditions) {
          errors.push({
            path: target.token,
            name: "duplicate",
            message: `Target is the same as target #${i + 1}. Targets cannot have the same conditions and country.`,
          });
          break;
        }
      }
    });
    return errors;
  });

  const selectedTargetValidationErrors = computed(() =>
    showValidation.value ? targetsValidationErrors.value.filter((e) => e.path === selectedTarget.value?.token) : [],
  );

  const targetsErrors = computed(() => {
    // Merge frontend validation errors + any backend errors
    const backendErrors = targets.value.flatMap((t) => t.errors ?? []);
    return [...targetsValidationErrors.value, ...backendErrors].map((e) => ({
      index: targets.value.findIndex((t) => t.token === e.path) + 1,
      message: e.message,
    }));
  });

  const hasTargetsErrors = computed(() => targetsErrors.value.length > 0);

  return {
    fetch,
    hydrate,
    save,
    fetchedSurveyToken,

    // country
    countryOptions,
    updateTargetCountries,

    // targets
    selectedTarget,
    currentTargets,
    hasTargetsChanged,
    hasSingleTarget,
    isMultiCountry,
    targetCountries,
    isCardBindedToTarget,
    addNewTarget,
    duplicateTarget,
    updateSelectedTarget,
    removeTarget,
    isTargetSelected,
    selectedTargetIndex,

    // CONDITIONS
    currentTargetsConditions,
    handleAddCondition,
    removeCondition,
    // question
    conditionQuestionModel,
    conditionQuestionOptions,
    updateConditionQuestion,
    // type
    typeOptions,
    updateConditionType,
    // values
    conditionValuesOptions,
    updateConditionValues,
    // operator
    operatorOptions,
    updateOperator,

    // errors
    hasTargetsErrors,
    selectedTargetValidationErrors,
  };
});
